/**
 * The office's Companion close-out sheet: the five questions when the visit happened, or — 26 Oct 2026
 * (D-1026-VISIONS, Eli) — "It didn't happen" with a reason. A visit that did not happen never goes on the VISIONS
 * report. Used on the Companion tab (Needs your attention) and in the VISIONS review's "Waiting for close-out".
 */
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DIDNT_HAPPEN_REASONS, type CompanionIncidentInput, type CompletionInput, type DidntHappenReason } from '@shared/volunteers';
import { useAnnounce } from '@/components/Announcer';
import { Button } from '@/components/ui/button';
import { SheetContent } from '@/components/ui/sheet';
import { useToast } from '@/components/ui/use-toast';
import { errorText } from '@/features/people/api';
import { ChoiceField, TextArea } from '@/features/people/fields';
import { postJson, useVolunteerRefresh } from './api';
import { CompletionForm } from './CompletionForm';
import { IncidentForm } from './IncidentForm';
import { sendIncidentPhotos } from './incidentPhotos';

export type EntryMode = 'completion' | 'incident';
export interface CloseoutEntry {
  appointmentId: string;
  volunteerId: string;
  label: string;
  mode: EntryMode;
  /** 1104: when the visit was booked to start (the form shows it and starts "What time did you start?" there) */
  minutes?: number;
}

export function EntrySheet({ entry, onClose }: { entry: CloseoutEntry; onClose: () => void }) {
  const { t } = useTranslation();
  const announce = useAnnounce();
  const toast = useToast();
  const refresh = useVolunteerRefresh();
  const uid = useId();
  const [mode, setMode] = useState<EntryMode>(entry.mode);
  const [happened, setHappened] = useState<'yes' | 'no' | ''>('yes');
  const [reason, setReason] = useState<DidntHappenReason | ''>('');
  const [notes, setNotes] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [incidentId, setIncidentId] = useState<string | null>(null);
  const sendCompletion = async (c: CompletionInput) => {
    setBusy(true);
    try {
      const r = await postJson<{ minutes: number; needIncident: boolean }>(`/api/volunteers/visits/${entry.appointmentId}`, { what: 'completion', completion: c });
      const m = t('vol.done.logged', { minutes: r.minutes });
      toast(m);
      announce(m);
      refresh();
      if (r.needIncident) setMode('incident');
      else onClose();
    } catch (e) {
      announce(errorText(e), 'assertive');
    } finally {
      setBusy(false);
    }
  };
  const sendDidnt = async () => {
    if (!reason) {
      const m = t('visions1026.needReason');
      setErr(m);
      announce(m, 'assertive');
      return;
    }
    setErr(null);
    setBusy(true);
    try {
      await postJson(`/api/volunteers/visits/${entry.appointmentId}`, { what: 'didnt-happen', didnt: { reason, notes } });
      const m = t('visions1026.saved');
      toast(m);
      announce(m);
      refresh();
      onClose();
    } catch (e) {
      const m = errorText(e);
      setErr(m);
      announce(m, 'assertive');
    } finally {
      setBusy(false);
    }
  };
  return (
    <SheetContent title={mode === 'completion' ? t('vol.done.staffTitle', { who: entry.label }) : t('vol.inc.staffTitle', { who: entry.label })} wide>
      {mode === 'completion' ? (
        <>
          <ChoiceField
            id={`${uid}-happened`}
            label={t('visions1026.happenedLegend')}
            value={happened}
            onChange={setHappened}
            options={[
              { value: 'yes', label: t('visions1026.happened') },
              { value: 'no', label: t('visions1026.didnt') },
            ]}
          />
          {happened === 'no' ? (
            <div data-testid="didnt-happen-form">
              <p className="help">{t('visions1026.didntHelp')}</p>
              <ChoiceField id={`${uid}-why`} label={t('visions1026.reasonLegend')} required value={reason} onChange={setReason} options={DIDNT_HAPPEN_REASONS.map((v) => ({ value: v, label: t(`visions1026.reason.${v}`) }))} />
              <TextArea id={`${uid}-dnotes`} label={t('visions1026.notes')} value={notes} onChange={setNotes} />
              {err ? (
                <p role="alert" className="field-err" data-testid="didnt-happen-err">
                  {err}
                </p>
              ) : null}
              <Button type="button" disabled={busy} onClick={() => void sendDidnt()} data-testid="didnt-happen-save">
                {t('visions1026.save')}
              </Button>
            </div>
          ) : (
            <>
              <p className="help">{t('vol.done.staffHelp')}</p>
              <CompletionForm onSubmit={(c) => void sendCompletion(c)} busy={busy} submitLabel={t('vol.done.record')} scheduledMinutes={entry.minutes} />
            </>
          )}
        </>
      ) : (
        <IncidentForm
          facts={{ day: '', time: '', tenant: entry.label, volunteer: entry.label.split(' · ')[0] ?? '', activity: '' }}
          busy={busy}
          onSubmit={async (report: CompanionIncidentInput) => {
            setBusy(true);
            try {
              const r = await postJson<{ incidentId: string }>('/api/volunteers/incidents', { appointmentId: entry.appointmentId, volunteerId: entry.volunteerId, report });
              setIncidentId(r.incidentId);
              refresh();
              announce(t('vol.inc.sentTitle'));
              return true;
            } catch (e) {
              announce(errorText(e), 'assertive');
              return false;
            } finally {
              setBusy(false);
            }
          }}
          // 17 Oct 2026: a photo is added only when the server's answer says so (an error or no connection is not)
          onPhotos={(files) =>
            sendIncidentPhotos(files, (f) => {
              const fd = new FormData();
              fd.set('file', f);
              return fetch(`/api/volunteers/incidents/${incidentId}`, { method: 'POST', body: fd, credentials: 'same-origin', headers: { Accept: 'application/json' } });
            })
          }
        />
      )}
    </SheetContent>
  );
}
